import {
  calloutBindingId,
  type CalloutBinding,
  type CalloutShape,
} from "sketch/shapes/callout";
import {
  StateNode,
  Vec,
  createShapeId,
  maybeSnapToGrid,
  type TLBindingId,
  type TLShape,
  type TLShapeId,
  type TLUiIconJsx,
  type TLUiOverrides,
  type TLUiToolItem,
} from "tldraw";

// Not `tlui-icon`: that class masks a background color, blanking JSX icons.
// A leader: arrow, leg, landing, and the lettering it carries.
const CalloutIcon: TLUiIconJsx = (
  <div style={{ width: 18, height: 18 }}>
    <svg viewBox="0 0 18 18" width="100%" height="100%" fill="none">
      <path
        d="M 3 15 L 9 9 L 16 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M 2.5 15.5 L 6.5 14.5 L 5 12.5 Z" fill="currentColor" />
      <line
        x1="9"
        y1="5"
        x2="16"
        y2="5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const HIT_MARGIN = 8;

interface PickedTarget {
  point: Vec;
  target?: TLShape;
  anchor?: { x: number; y: number };
}

/**
 * The callout tool, in the order a drafter works: click what the note is
 * about (whatever the cursor would bind to is highlighted, the same hinting
 * a dim gives), then click where the lettering goes, then type it. The
 * leader binds to what you pointed at, so it follows that element from then
 * on. The style panel decides the head: plain lettering, or a keynote tag.
 */
export class CalloutTool extends StateNode {
  static override id = "callout";
  /** Ties the tool to the callout's style props, so the style panel shows
   *  the tag and terminator pickers while the tool is active. */
  override shapeType = "callout";

  private provisional: TLShapeId | null = null;
  private first: PickedTarget | null = null;

  override onEnter() {
    this.reset();
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }

  override onExit() {
    this.cancelProvisional();
    this.editor.setHintingShapes([]);
    this.editor.setCursor({ type: "default", rotation: 0 });
  }

  override onKeyDown(info: { key: string }) {
    if (info.key !== "Escape") return;
    if (this.provisional) {
      this.cancelProvisional();
      return;
    }
    this.editor.setCurrentTool("select");
  }

  override onCancel() {
    this.cancelProvisional();
    this.editor.setCurrentTool("select");
  }

  override onPointerMove() {
    if (!this.provisional) {
      const hover = this.pick();
      this.editor.setHintingShapes(hover.target ? [hover.target.id] : []);
      return;
    }
    const point = maybeSnapToGrid(
      this.editor.inputs.currentPagePoint,
      this.editor,
    );
    this.editor.updateShape<CalloutShape>({
      id: this.provisional,
      type: "callout",
      props: { head: { x: point.x, y: point.y } },
    });
  }

  override onPointerDown() {
    if (this.provisional) {
      this.finalize();
      return;
    }
    this.startProvisional(this.pick());
  }

  private reset() {
    this.provisional = null;
    this.first = null;
  }

  private cancelProvisional() {
    if (this.provisional) this.editor.deleteShapes([this.provisional]);
    this.reset();
  }

  /** What the leader would land on: a shape (anchored where the cursor is
   *  inside it, since a note points at a surface as often as an edge) or a
   *  free point on the graph paper. */
  private pick(): PickedTarget {
    const point = Vec.From(this.editor.inputs.currentPagePoint);
    const target = this.editor.getShapeAtPoint(point, {
      hitInside: true,
      hitLocked: true,
      margin: HIT_MARGIN,
      filter: (shape) =>
        shape.type !== "callout" &&
        shape.type !== "dim" &&
        shape.id !== this.provisional,
    });
    const bounds = target && this.editor.getShapePageBounds(target.id);
    if (!target || !bounds) {
      return { point: maybeSnapToGrid(point, this.editor) };
    }
    return {
      point,
      target,
      anchor: {
        x: bounds.w ? clamp01((point.x - bounds.x) / bounds.w) : 0.5,
        y: bounds.h ? clamp01((point.y - bounds.y) / bounds.h) : 0.5,
      },
    };
  }

  private startProvisional(pick: PickedTarget) {
    const id = createShapeId();
    this.editor.markHistoryStoppingPoint(`callout:${id}`);
    this.editor.createShape<CalloutShape>({
      id,
      type: "callout",
      x: 0,
      y: 0,
      props: {
        text: "",
        head: { x: pick.point.x, y: pick.point.y },
        target: { x: pick.point.x, y: pick.point.y },
      },
    });
    this.first = pick;
    this.provisional = id;
  }

  private finalize() {
    const id = this.provisional;
    const first = this.first;
    this.reset();
    if (!id || !first) return;
    const head = maybeSnapToGrid(
      this.editor.inputs.currentPagePoint,
      this.editor,
    );
    this.editor.updateShape<CalloutShape>({
      id,
      type: "callout",
      props: { head: { x: head.x, y: head.y } },
    });
    if (first.target) {
      this.editor.createBindings<CalloutBinding>([
        {
          // The deterministic id every write path shares, so a later re-bind
          // (from the agent or the UI) replaces rather than stacks.
          id: calloutBindingId(id) as TLBindingId,
          type: "callout",
          fromId: id,
          toId: first.target.id,
          props: { anchor: first.anchor ?? { x: 0.5, y: 0.5 } },
        },
      ]);
    }
    // The note is the point of the callout: land it and type it. The tool
    // stays active, so the next one starts with a click, like the gridline.
    this.editor.setEditingShape(id);
  }
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export const calloutToolOverride: TLUiOverrides = {
  translations: {
    en: {
      "tool.callout": "Callout",
      "style-panel.calloutTag": "Head",
      "style-panel.calloutTerminator": "Leader",
      "calloutTag-style.none": "Lettering",
      "calloutTag-style.circle": "Circle tag",
      "calloutTag-style.hex": "Hex tag",
      "calloutTerminator-style.arrow": "Arrow",
      "calloutTerminator-style.dot": "Dot",
      "calloutTerminator-style.none": "None",
    },
  },
  tools(editor, tools) {
    tools.callout = {
      id: CalloutTool.id,
      label: "tool.callout",
      icon: CalloutIcon,
      // "n" for note: the sticky note it replaces used to hold that key.
      kbd: "n",
      onSelect() {
        editor.setCurrentTool(CalloutTool.id);
      },
    } satisfies TLUiToolItem;
    // The sticky note is not a construction document annotation: fixed
    // yellow squares sized in world feet say nothing a leader note doesn't
    // say better. Dropping the tool item takes it off the toolbar and out
    // of the keymap; old sketches holding one still open and read.
    delete tools.note;
    return tools;
  },
};
